<template>
  <div class="flex flex-col h-full bg-gray-50">
    <!-- Top bar -->
    <div class="flex items-center gap-4 px-4 py-3 bg-white border-b">
      <button
        @click="goBack"
        class="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Zurück zur App
      </button>
      <h1 class="text-xl font-bold text-gray-800">Handbuch</h1>
      <!-- Chapter selector (mobile & desktop) -->
      <select
        v-model="currentSection"
        @change="navigateToSection"
        class="ml-auto p-1.5 text-sm border rounded-lg max-w-xs"
      >
        <option value="">Kapitel wählen...</option>
        <optgroup v-for="group in toc" :key="group.group" :label="group.group">
          <option v-for="item in group.items" :key="item.id" :value="item.id">
            {{ item.title }}
          </option>
        </optgroup>
      </select>
    </div>

    <!-- 3-column layout -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Left chapter sidebar -->
      <aside class="hidden sm:block w-56 flex-shrink-0 bg-secondary overflow-y-auto">
        <div class="p-4">
          <h2 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Inhalt</h2>
          <div v-for="group in toc" :key="group.group" class="mb-4">
            <h3 class="text-xs font-semibold text-gray-400 uppercase mb-2">{{ group.group }}</h3>
            <nav class="space-y-1">
              <a
                v-for="item in group.items"
                :key="item.id"
                href="#"
                @click.prevent="loadSection(item.id)"
                :class="[
                  'block rounded-lg p-3 text-sm font-medium transition-colors',
                  currentSection === item.id
                    ? 'bg-primary text-secondary'
                    : 'text-gray-300 hover:bg-gray-700'
                ]"
              >
                {{ item.title }}
              </a>
            </nav>
          </div>
        </div>
      </aside>

      <!-- Main content area -->
      <main class="flex-1 overflow-y-auto p-6 lg:p-8">
        <div class="max-w-3xl mx-auto">
          <!-- Search bar -->
          <div class="mb-6">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Handbuch durchsuchen..."
              class="w-full p-3 border rounded-lg"
              @input="handleSearch"
            />
            <div v-if="searchResults.length > 0" class="mt-2 bg-white border rounded-lg shadow">
              <a
                v-for="result in searchResults"
                :key="result.item.id"
                href="#"
                @click.prevent="loadSection(result.item.id)"
                class="block p-3 hover:bg-gray-50 border-b last:border-b-0"
              >
                <div class="font-medium">{{ result.item.title }}</div>
                <div class="text-sm text-gray-500">{{ result.item.group }}</div>
              </a>
            </div>
          </div>

          <!-- Content -->
          <div v-if="loading" class="text-center py-12">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
          <div v-else-if="error" class="text-center py-12 text-red-600">
            {{ error }}
          </div>
          <div v-else-if="content" class="prose prose-lg max-w-none">
            <h1 class="text-3xl font-bold mb-4">{{ contentTitle }}</h1>
            <div v-html="renderedContent" class="handbook-content"></div>
            
            <!-- Navigation buttons -->
            <div class="flex justify-between mt-12 pt-6 border-t">
              <button
                v-if="prevSection"
                @click="loadSection(prevSection.id)"
                class="px-4 py-2 text-primary hover:underline"
              >
                ← {{ prevSection.title }}
              </button>
              <button
                v-if="nextSection"
                @click="loadSection(nextSection.id)"
                class="px-4 py-2 text-primary hover:underline ml-auto"
              >
                {{ nextSection.title }} →
              </button>
            </div>

            <!-- Last updated footer -->
            <div class="mt-8 pt-6 border-t text-sm text-gray-500">
              Zuletzt aktualisiert: {{ contentLastUpdated }}
            </div>
          </div>
        </div>
      </main>

      <!-- Right "On this page" sidebar -->
      <aside class="hidden lg:block w-52 flex-shrink-0 overflow-y-auto">
        <div class="p-4">
          <h2 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Auf dieser Seite</h2>
          <nav class="space-y-2">
            <a
              v-for="heading in headings"
              :key="heading.id"
              :href="'#' + heading.id"
              :class="[
                'block text-sm transition-colors',
                heading.level === 3 ? 'pl-3' : '',
                activeHeading === heading.id 
                  ? 'text-primary font-medium' 
                  : 'text-gray-600 hover:text-gray-900'
              ]"
              @click.prevent="scrollToHeading(heading.id)"
            >
              {{ heading.text }}
            </a>
          </nav>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import Fuse from 'fuse.js'

export default {
  name: 'HandbookView',
  data() {
    return {
      toc: [],
      content: '',
      renderedContent: '',
      contentTitle: '',
      contentLastUpdated: '',
      currentSection: '',
      loading: false,
      error: null,
      headings: [],
      activeHeading: '',
      searchQuery: '',
      searchResults: [],
      fuse: null,
      observer: null
    }
  },
  computed: {
    context() {
      return this.$route.meta.context || 'business'
    },
    allSections() {
      const sections = []
      this.toc.forEach(group => {
        group.items.forEach(item => {
          sections.push({ ...item, group: group.group })
        })
      })
      return sections
    },
    currentIndex() {
      return this.allSections.findIndex(s => s.id === this.currentSection)
    },
    prevSection() {
      if (this.currentIndex > 0) {
        return this.allSections[this.currentIndex - 1]
      }
      return null
    },
    nextSection() {
      if (this.currentIndex < this.allSections.length - 1) {
        return this.allSections[this.currentIndex + 1]
      }
      return null
    }
  },
  watch: {
    renderedContent() {
      this.$nextTick(() => {
        this.extractHeadings()
      })
    }
  },
  async mounted() {
    await this.loadTOC()
    this.setupIntersectionObserver()

    // Load initial section from query param or first section
    const sectionId = this.$route.query.section || this.toc[0]?.items[0]?.id
    if (sectionId) {
      await this.loadSection(sectionId)
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    async loadTOC() {
      try {
        const response = await fetch(`/handbook/${this.context}/index.json`)
        if (!response.ok) throw new Error('TOC konnte nicht geladen werden')
        this.toc = await response.json()
        
        // Initialize Fuse.js for search
        this.fuse = new Fuse(this.allSections, {
          keys: ['title', 'group'],
          threshold: 0.3
        })
      } catch (err) {
        this.error = 'Fehler beim Laden des Inhaltsverzeichnisses'
        console.error(err)
      }
    },
    async loadSection(sectionId) {
      this.loading = true
      this.error = null
      this.currentSection = sectionId
      
      // Update URL query param
      this.$router.replace({ query: { section: sectionId } })
      
      try {
        const response = await fetch(`/handbook/${this.context}/${sectionId}.md`)
        if (!response.ok) throw new Error('Abschnitt konnte nicht geladen werden')
        const markdown = await response.text()
        
        // Parse frontmatter
        const { content, data } = this.parseFrontmatter(markdown)
        this.content = content
        this.contentTitle = data.title || sectionId
        this.contentLastUpdated = data.lastUpdated || ''
        
        // Render markdown
        const html = marked(content)
        this.renderedContent = DOMPurify.sanitize(html)
      } catch (err) {
        this.error = 'Fehler beim Laden des Abschnitts'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    parseFrontmatter(markdown) {
      const frontmatterRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/
      const match = markdown.match(frontmatterRegex)
      
      if (match) {
        const frontmatter = match[1]
        const content = match[2]
        const data = {}
        
        frontmatter.split('\n').forEach(line => {
          const [key, ...valueParts] = line.split(':')
          if (key && valueParts.length) {
            const value = valueParts.join(':').trim()
            data[key.trim()] = value
          }
        })
        
        return { content, data }
      }
      
      return { content: markdown, data: {} }
    },
    extractHeadings() {
      const container = this.$el.querySelector('.handbook-content')
      if (!container) return

      if (this.observer) {
        this.observer.disconnect()
      }

      const headings = []
      const elements = container.querySelectorAll('h2, h3')

      elements.forEach((el, index) => {
        const id = `heading-${index}`
        el.id = id
        headings.push({
          id,
          text: el.textContent,
          level: parseInt(el.tagName.charAt(1))
        })
      })

      this.headings = headings

      // Observe elements after they're in the DOM
      this.$nextTick(() => {
        if (this.observer) {
          elements.forEach(el => this.observer.observe(el))
        }
      })
    },
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.activeHeading = entry.target.id
            }
          })
        },
        {
          rootMargin: '-100px 0px -60% 0px'
        }
      )
    },
    scrollToHeading(id) {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
        
        // Observe the element after scrolling
        this.$nextTick(() => {
          if (this.observer) {
            this.observer.observe(element)
          }
        })
      }
    },
    handleSearch() {
      if (!this.searchQuery.trim()) {
        this.searchResults = []
        return
      }
      
      if (this.fuse) {
        this.searchResults = this.fuse.search(this.searchQuery)
      }
    },
    navigateToSection() {
      if (this.currentSection) {
        this.loadSection(this.currentSection)
      }
    },
    goBack() {
      const fallback = this.context === 'business' ? '/b/' : '/c/overview'
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push(fallback)
      }
    }
  }
}
</script>

<style scoped>
.handbook-content :deep(h2) {
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.handbook-content :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.handbook-content :deep(p) {
  margin-bottom: 1rem;
  line-height: 1.75;
}

.handbook-content :deep(ul),
.handbook-content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  list-style-position: outside;
}

.handbook-content :deep(ul) {
  list-style-type: disc;
}

.handbook-content :deep(ol) {
  list-style-type: decimal;
}

.handbook-content :deep(li) {
  margin-bottom: 0.5rem;
}

.handbook-content :deep(code) {
  background-color: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.handbook-content :deep(pre) {
  background-color: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin-bottom: 1rem;
}

.handbook-content :deep(pre code) {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.handbook-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.handbook-content :deep(th),
.handbook-content :deep(td) {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  text-align: left;
}

.handbook-content :deep(th) {
  background-color: #f9fafb;
  font-weight: 600;
}

.handbook-content :deep(a) {
  color: #f97316;
  text-decoration: underline;
}

.handbook-content :deep(a:hover) {
  color: #ea580c;
}

.handbook-content :deep(blockquote) {
  border-left: 4px solid #f97316;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #6b7280;
  font-style: italic;
}
</style>
